/**
 * Alpine collection filter manager v2.
 */

const SNAPSHOT_KEY = 'ls_listing_snapshot_v2';
const SCROLL_SNAPSHOT_KEY = 'ls_listing_scroll_v2';
const RETURN_INTENT_KEY = 'ls_listing_return_intent';
const LAST_PRODUCT_HANDLE_KEY = 'ls_listing_last_product_handle';
const SNAPSHOT_MAX_AGE_MS = 30 * 60 * 1000;
const LONG_LIST_THRESHOLD = 12;
const PROGRAMMATIC_SCROLL_GUARD_MS = 500;
const QUICK_FILTER_IDS = ['gender', 'category', 'size', 'brand', 'product_type', 'colour', 'condition'];

function getMenuFilterOrder() {
  const fromConfig = window.COLLECTION_V2_CONFIG?.menuFilterOrder;
  if (Array.isArray(fromConfig) && fromConfig.length) return fromConfig;
  return ['sort', ...QUICK_FILTER_IDS, 'price'];
}

const COLOUR_SWATCH_HEX = {
  beige: '#D4B896',
  black: '#1A1A1A',
  blue: '#2563EB',
  brown: '#6B4423',
  burgundy: '#722F37',
  'burnt orange': '#CC5500',
  charcoal: '#36454F',
  cream: '#FFFDD0',
  green: '#228B22',
  grey: '#808080',
  khaki: '#6B7C4C',
  mauve: '#E0B0FF',
  navy: '#1B2A4A',
  nude: '#E3BC9A',
  orange: '#FF8C00',
  pink: '#FFC0CB',
  purple: '#800080',
  red: '#DC143C',
  salmon: '#FA8072',
  tan: '#D2B48C',
  white: '#FFFFFF',
  yellow: '#FFD700'
};

const COLOUR_SWATCH_GRADIENTS = {
  gold: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',
  silver: 'linear-gradient(135deg, #E8E8E8 0%, #A8A8A8 100%)',
  'rose gold': 'linear-gradient(135deg, #E8B4B8 0%, #B76E79 100%)'
};

const COLOUR_SWATCH_BORDERED = new Set(['white']);
const COLOUR_SWATCH_MULTI = 'multi-coloured';
const COLOUR_SWATCH_FALLBACK = '#9CA3AF';

function resolveColourSwatch(name) {
  const key = String(name || '').trim().toLowerCase();
  if (key === COLOUR_SWATCH_MULTI) {
    return { background: 'transparent', bordered: false, multi: true };
  }
  if (COLOUR_SWATCH_GRADIENTS[key]) {
    return { background: COLOUR_SWATCH_GRADIENTS[key], bordered: false, multi: false };
  }
  const hex = COLOUR_SWATCH_HEX[key];
  if (!hex) {
    return { background: COLOUR_SWATCH_FALLBACK, bordered: false, multi: false };
  }
  return {
    background: hex,
    bordered: COLOUR_SWATCH_BORDERED.has(key),
    multi: false
  };
}

function canonicalizeListingUrl(pathAndSearch) {
  const qIndex = pathAndSearch.indexOf('?');
  const pathname = qIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, qIndex);
  const search = qIndex === -1 ? '' : pathAndSearch.slice(qIndex + 1);
  const params = new URLSearchParams(search);
  const sorted = [...params.entries()].sort(
    (a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1])
  );
  const normalized = new URLSearchParams();
  sorted.forEach(([key, value]) => normalized.append(key, value));
  const queryString = normalized.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

function urlsMatchExactly(storedUrl, currentUrl) {
  return canonicalizeListingUrl(storedUrl || '') === canonicalizeListingUrl(currentUrl || '');
}

function isBackForwardNavigation() {
  const entry = performance.getEntriesByType('navigation')[0];
  return entry?.type === 'back_forward';
}

function setReturnIntent(url, productHandle) {
  try {
    const canonical = canonicalizeListingUrl(url);
    sessionStorage.setItem(
      RETURN_INTENT_KEY,
      JSON.stringify({ url: canonical, savedAt: Date.now() })
    );
    if (productHandle) {
      sessionStorage.setItem(
        LAST_PRODUCT_HANDLE_KEY,
        JSON.stringify({ url: canonical, handle: productHandle, savedAt: Date.now() })
      );
    }
  } catch (e) {}
}

function hasValidReturnIntent(currentUrl) {
  try {
    const raw = sessionStorage.getItem(RETURN_INTENT_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (Date.now() - data.savedAt > SNAPSHOT_MAX_AGE_MS) return false;
    return urlsMatchExactly(data.url, currentUrl);
  } catch (e) {
    return false;
  }
}

function clearReturnIntent() {
  try {
    sessionStorage.removeItem(RETURN_INTENT_KEY);
    sessionStorage.removeItem(LAST_PRODUCT_HANDLE_KEY);
  } catch (e) {}
}

function shouldRestoreListingNavigation(currentUrl) {
  return isBackForwardNavigation() || hasValidReturnIntent(currentUrl);
}

function extractProductHandleFromLink(link) {
  const card =
    link.querySelector('[data-product-handle]') || link.closest('[data-product-handle]');
  return card?.getAttribute('data-product-handle') || null;
}

function tryEarlyScrollRestore(options = {}) {
  if (typeof window.LSCollectionV2EarlyRestore === 'function') {
    window.LSCollectionV2EarlyRestore(options);
    return;
  }

  const currentUrl = window.location.pathname + window.location.search;
  if (!options.force && !shouldRestoreListingNavigation(currentUrl)) return;

  const scrollY = readScrollSnapshot();
  if (scrollY == null || scrollY <= 0) return;

  if (options.force && Math.abs(currentScrollY() - scrollY) <= 8) return;

  applyScrollPosition(scrollY);
  window.__LS_COLLECTION_V2_PENDING_SCROLL = scrollY;
}

function tryScrollToLastProductCard(targetScrollY) {
  if (Math.abs(currentScrollY() - targetScrollY) <= 8) {
    clearReturnIntent();
    return;
  }

  let handle;
  try {
    const raw = sessionStorage.getItem(LAST_PRODUCT_HANDLE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    const currentUrl = window.location.pathname + window.location.search;
    if (!urlsMatchExactly(data.url, currentUrl)) return;
    if (Date.now() - data.savedAt > SNAPSHOT_MAX_AGE_MS) return;
    handle = data.handle;
  } catch (e) {
    return;
  }

  if (!handle) return;

  const card = document.querySelector(`#collection-product-grid [data-product-handle="${handle}"]`);
  if (!card) return;

  const headerHeight =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  const targetY = Math.max(0, getDocumentOffsetTop(card) - headerHeight - 8);
  applyScrollPosition(targetY);
  clearReturnIntent();
}

function clearStaleListingSnapshots() {
  const currentUrl = window.location.pathname + window.location.search;

  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (raw) {
      const snapshot = JSON.parse(raw);
      if (!urlsMatchExactly(snapshot.url, currentUrl)) {
        sessionStorage.removeItem(SNAPSHOT_KEY);
      }
    }
  } catch (e) {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  }

  try {
    const raw = sessionStorage.getItem(SCROLL_SNAPSHOT_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (!urlsMatchExactly(data.url, currentUrl)) {
        sessionStorage.removeItem(SCROLL_SNAPSHOT_KEY);
      }
    }
  } catch (e) {
    sessionStorage.removeItem(SCROLL_SNAPSHOT_KEY);
  }
}

function isIOSWebKit() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isIOSSafari() {
  if (!isIOSWebKit()) return false;
  const ua = navigator.userAgent;
  return /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function isSafariEngine() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Android/.test(ua)) return false;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return /Safari/.test(ua) || (isIOS && /WebKit/.test(ua));
}

function currentScrollY() {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function applyScrollPosition(y) {
  const scrollY = Math.max(0, y || 0);
  window.scrollTo(0, scrollY);
  document.documentElement.scrollTop = scrollY;
  document.body.scrollTop = scrollY;
}

function getDocumentOffsetTop(el) {
  let top = 0;
  let node = el;
  while (node) {
    top += node.offsetTop || 0;
    node = node.offsetParent;
  }
  return top;
}

function getListingChromeScrollY() {
  const sentinel = document.querySelector('[data-listing-chrome-sentinel]');
  if (!sentinel) return 0;

  const headerHeight = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height')
  ) || 0;
  const stickyOffset = headerHeight - 1;
  const top = getDocumentOffsetTop(sentinel) || (sentinel.getBoundingClientRect().top + currentScrollY());
  return Math.max(0, Math.round(top - stickyOffset));
}

function shouldRestoreScrollPosition(currentY, targetY, { onlyCorrectUpwardDrift = false } = {}) {
  if (onlyCorrectUpwardDrift) {
    return currentY < targetY - 8;
  }
  return Math.abs(currentY - targetY) > 8;
}

let activeScrollRestore = null;
let programmaticScrollUntil = 0;

function isProgrammaticScroll() {
  return Date.now() < programmaticScrollUntil;
}

function cancelActiveScrollRestore() {
  if (!activeScrollRestore) return;
  activeScrollRestore.cancelled = true;
  if (activeScrollRestore.rafId) cancelAnimationFrame(activeScrollRestore.rafId);
  activeScrollRestore.timeoutIds.forEach(clearTimeout);
  if (activeScrollRestore.cleanupTakeover) activeScrollRestore.cleanupTakeover();
  activeScrollRestore = null;
}

function bindRestoreTakeover(onTakeover) {
  const opts = { passive: true, capture: true };
  const onUserScroll = () => {
    if (isProgrammaticScroll()) return;
    onTakeover();
  };

  window.addEventListener('touchmove', onUserScroll, opts);
  window.addEventListener('wheel', onUserScroll, opts);

  return () => {
    window.removeEventListener('touchmove', onUserScroll, opts);
    window.removeEventListener('wheel', onUserScroll, opts);
  };
}

function restoreScrollPosition(y, { durationMs = 1200, onlyCorrectUpwardDrift = false, onUserTakeover } = {}) {
  const scrollY = y || 0;
  if (scrollY <= 0) return null;

  cancelActiveScrollRestore();

  const state = { cancelled: false, rafId: null, timeoutIds: [], cleanupTakeover: null };
  activeScrollRestore = state;

  const finish = () => {
    if (state.cleanupTakeover) state.cleanupTakeover();
    if (activeScrollRestore === state) activeScrollRestore = null;
  };

  state.cleanupTakeover = bindRestoreTakeover(() => {
    state.cancelled = true;
    if (onUserTakeover) onUserTakeover();
    finish();
  });

  programmaticScrollUntil = Date.now() + 80;
  applyScrollPosition(scrollY);

  let stableFrames = 0;
  const startedAt = Date.now();

  const tick = () => {
    if (state.cancelled) return;
    if (Date.now() - startedAt > durationMs) {
      finish();
      return;
    }

    const current = currentScrollY();
    if (!shouldRestoreScrollPosition(current, scrollY, { onlyCorrectUpwardDrift })) {
      stableFrames += 1;
      if (stableFrames >= 4) {
        finish();
        return;
      }
    } else {
      stableFrames = 0;
      programmaticScrollUntil = Date.now() + 80;
      applyScrollPosition(scrollY);
    }

    state.rafId = requestAnimationFrame(tick);
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(tick);
  });

  [0, 50, 100, 250, 500, 800, 1200, 1800].forEach((delay) => {
    if (delay > durationMs) return;
    const id = setTimeout(() => {
      if (state.cancelled) return;
      if (shouldRestoreScrollPosition(currentScrollY(), scrollY, { onlyCorrectUpwardDrift })) {
        programmaticScrollUntil = Date.now() + 80;
        applyScrollPosition(scrollY);
      }
    }, delay);
    state.timeoutIds.push(id);
  });

  return finish;
}

function readScrollSnapshot() {
  try {
    const raw = sessionStorage.getItem(SCROLL_SNAPSHOT_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    const currentUrl = window.location.pathname + window.location.search;
    if (!urlsMatchExactly(data.url, currentUrl)) return null;
    if (Date.now() - data.savedAt > SNAPSHOT_MAX_AGE_MS) return null;

    return data.scrollY || 0;
  } catch (e) {
    return null;
  }
}

window.LSCollectionV2Navigation = {
  tryEarlyScrollRestore
};

document.addEventListener('alpine:init', () => {
  console.log('[CollectionV2] Alpine registered — collectionFilter component ready');

  Alpine.data('collectionFilter', () => ({
    loading: false,
    hydrated: false,
    restoredFromBFCache: false,
    drawerOpen: false,
    sheetView: null,
    products: [],
    facets: [],
    baselineFilters: [],
    pending: { filters: {}, price: null },
    applied: { filters: {}, price: null },
    sort: null,
    sortGroups: [],
    totalProducts: 0,
    previewCount: 0,
    page: 1,
    totalPages: 0,
    openAccordion: null,
    filterApplicationOrder: [],
    isInitialLoad: true,
    lastApplied: null,
    previewRequestId: 0,
    fetchRequestId: 0,
    lastRenderedPage: 0,
    scrollSaveTimer: null,
    pendingScrollRestore: null,
    programmaticScrollUntil: 0,
    priceDebounce: null,
    adapter: null,
    renderer: null,
    listingType: 'collection',
    listingQuery: '',
    isSearchPage: false,
    activeFilterSheet: null,
    filterSheetSearchQuery: '',
    filterSheetFromMenu: false,
    sheetShellCloseIntent: null,
    saveSearchSubmitting: false,
    saveSearchError: '',
    saveSearchNameLength: 0,
    saveSearchSuccess: false,
    saveSearchSuccessName: '',
    saveSearchDissolving: false,
    saveSearchCloseTimer: null,

    get filterCount() {
      const filterKeys = Object.keys(this.applied.filters || {});
      const count = filterKeys.reduce((sum, key) => sum + (this.applied.filters[key]?.length || 0), 0);
      return count + (this.applied.price ? 1 : 0);
    },

    get canSaveSearch() {
      if (this.isSearchPage) {
        const query = (this.listingQuery || new URLSearchParams(window.location.search).get('q') || '').trim();
        return Boolean(query);
      }
      return this.filterCount > 0;
    },

    get activePills() {
      const pills = [];
      Object.keys(this.pending.filters || {}).forEach(section => {
        (this.pending.filters[section] || []).forEach(value => {
          const filter = this.facets.find(f => f.id === section);
          const option = filter?.values?.find(v => v.value === value);
          pills.push({
            id: `${section}-${value}`,
            section,
            value,
            label: option?.label || value
          });
        });
      });
      if (this.pending.price) {
        const min = this.pending.price.min ?? 0;
        const max = this.pending.price.max ?? '∞';
        pills.push({
          id: 'price-range',
          section: 'price',
          value: 'range',
          label: `£${min} - £${max}`
        });
      }
      if (this.lastApplied) {
        const idx = pills.findIndex(p => p.section === this.lastApplied.section && p.value === this.lastApplied.value);
        if (idx > 0) pills.unshift(pills.splice(idx, 1)[0]);
      }
      return pills;
    },

    get sortLabel() {
      if (!this.sort) return '';
      for (const group of this.sortGroups) {
        const option = group.options.find(o => o.key === this.sort);
        if (option) return option.label;
      }
      return SORT_LABELS[this.sort] || '';
    },

    get defaultSortId() {
      if (this.isSearchPage) return null;
      const raw = window.COLLECTION_V2_CONFIG?.defaultSort;
      if (!raw || !this.adapter) return null;
      return this.adapter.toCanonicalSortId(raw, this.getSortContext()) || raw;
    },

    get hasNonDefaultSort() {
      if (!this.sort) return false;
      const defaultId = this.defaultSortId;
      if (!defaultId) return true;
      return this.sort !== defaultId;
    },

    get visibleFacets() {
      return (this.facets || []).filter(f => this.shouldShowFilter(f));
    },

    get previewResultsLabel() {
      const count = this.previewCount;
      return `Show ${count} ${count === 1 ? 'item' : 'items'}`;
    },

    get perPage() {
      return window.COLLECTION_V2_CONFIG?.typesense?.productsPerPage || 50;
    },

    get resultsRangeStart() {
      if (!this.totalProducts) return 0;
      return (this.page - 1) * this.perPage + 1;
    },

    get resultsRangeEnd() {
      if (!this.totalProducts) return 0;
      return Math.min(this.page * this.perPage, this.totalProducts);
    },

    get filterBarItems() {
      const facets = QUICK_FILTER_IDS
        .map(id => this.facets.find(f => f.id === id))
        .filter(f => this.shouldShowQuickFilter(f));
      const facetById = new Map(facets.map(f => [f.id, f]));
      const priceFacet = this.getPriceFacet();
      const showPrice = priceFacet && this.shouldShowFilter(priceFacet);
      const items = [];
      const placed = new Set();

      [...this.filterApplicationOrder].reverse().forEach(section => {
        if (section === 'sort') return;
        if (section === 'price') {
          if (showPrice && this.appliedCount('price') > 0 && !placed.has('price')) {
            items.push({ type: 'price', id: 'price', facet: priceFacet });
            placed.add('price');
          }
          return;
        }

        const facet = facetById.get(section);
        if (facet && this.appliedCount(section) > 0 && !placed.has(section)) {
          items.push({ type: 'facet', id: facet.id, facet });
          placed.add(section);
        }
      });

      facets.forEach(facet => {
        if (this.appliedCount(facet.id) > 0 && !placed.has(facet.id)) {
          items.push({ type: 'facet', id: facet.id, facet });
          placed.add(facet.id);
        }
      });

      if (showPrice && this.appliedCount('price') > 0 && !placed.has('price')) {
        items.push({ type: 'price', id: 'price', facet: priceFacet });
        placed.add('price');
      }

      facets.forEach(facet => {
        if (!placed.has(facet.id)) {
          items.push({ type: 'facet', id: facet.id, facet });
          placed.add(facet.id);
        }
      });

      if (showPrice && !placed.has('price')) {
        items.push({ type: 'price', id: 'price', facet: priceFacet });
        placed.add('price');
      }

      return items;
    },

    get filterBarPillItems() {
      return this.filterBarItems;
    },

    get filtersMenuItems() {
      const priceFacet = this.getPriceFacet();
      const items = [];

      getMenuFilterOrder().forEach(id => {
        if (id === 'sort') {
          items.push({ type: 'sort', id: 'sort' });
          return;
        }

        if (id === 'price') {
          if (priceFacet && this.shouldShowFilter(priceFacet)) {
            items.push({ type: 'price', id: 'price', facet: priceFacet });
          }
          return;
        }

        const facet = this.facets.find(f => f.id === id);
        if (facet && this.shouldShowFilter(facet)) {
          items.push({ type: 'facet', id: facet.id, facet });
        }
      });

      return items;
    },

    get activeFilterFacet() {
      if (!this.activeFilterSheet) return null;
      return this.facets.find(f => f.id === this.activeFilterSheet) || null;
    },

    get filterSheetValues() {
      const facet = this.activeFilterFacet;
      if (!facet) return [];
      return this.visibleValues(facet);
    },

    get filteredFilterSheetGroups() {
      const facet = this.activeFilterFacet;
      if (!facet || facet.type !== 'grouped_option') return [];

      const query = (this.filterSheetSearchQuery || '').trim().toLowerCase();
      const groups = this.visibleGroups(facet);

      if (!query) return groups;

      const facetId = facet.id;
      const selectedValues = facetId ? (this.pending.filters[facetId] || []) : [];

      return groups
        .map(group => {
          const matched = group.values.filter(value => this.filterSheetValueMatchesQuery(value, query));
          const matchedSet = new Set(matched.map(value => value.value));

          selectedValues.forEach(selectedValue => {
            if (matchedSet.has(selectedValue)) return;
            const option = group.values.find(value => value.value === selectedValue);
            if (option) matched.push(option);
          });

          return matched.length ? { ...group, values: matched } : null;
        })
        .filter(Boolean);
    },

    get filterSheetHasGroupedValues() {
      return this.activeFilterFacet?.type === 'grouped_option'
        && (this.activeFilterFacet.groups || []).length > 0;
    },

    get filteredFilterSheetValues() {
      const values = this.filterSheetValues;
      const query = (this.filterSheetSearchQuery || '').trim().toLowerCase();
      if (!query) return values;

      const facetId = this.activeFilterFacet?.id;
      const selectedValues = facetId ? (this.pending.filters[facetId] || []) : [];
      const matched = values.filter(v => {
        const label = (v.label || v.value || '').toLowerCase();
        const value = (v.value || '').toLowerCase();
        return label.includes(query) || value.includes(query);
      });
      const matchedSet = new Set(matched.map(v => v.value));

      selectedValues.forEach(selectedValue => {
        if (matchedSet.has(selectedValue)) return;
        const option = values.find(v => v.value === selectedValue);
        if (option) matched.push(option);
      });

      return matched;
    },

    get filterSheetNeedsSearch() {
      if (this.activeFilterFacet?.id === 'size') return false;
      return this.filterSheetValues.length > LONG_LIST_THRESHOLD;
    },

    get filterSheetSearchPlaceholder() {
      const label = this.activeFilterFacet?.label || '';
      return label ? `Search ${label}…` : 'Search…';
    },

    get filterSheetNoMatchesLabel() {
      const query = (this.filterSheetSearchQuery || '').trim();
      if (!query) return '';
      if (this.filterSheetHasGroupedValues) {
        return this.filteredFilterSheetGroups.length === 0 ? `No matches for "${query}"` : '';
      }
      return `No matches for "${query}"`;
    },

    async init() {
      const root = this.$el;
      this.listingType = root.dataset.listingType || 'collection';
      this.listingQuery = root.dataset.listingQuery || '';
      this.isSearchPage = this.listingType === 'search';

      console.log('=== COLLECTION FILTER V2 (Alpine + Typesense) ===');
      console.log('[CollectionV2] Initializing', {
        type: this.listingType,
        query: this.listingQuery,
        enabled: window.COLLECTION_V2_CONFIG?.enabled
      });

      if (!window.COLLECTION_V2_CONFIG?.enabled) {
        console.warn('[CollectionV2] listing is disabled — check theme configuration');
      }

      this.adapter = new TypesenseAPIAdapterV2(window.COLLECTION_V2_CONFIG?.typesense || {});
      this.renderer = new CollectionProductRendererV2('#collection-product-grid');
      console.log('[CollectionV2] Adapter:', this.adapter.getName());

      if ('scrollRestoration' in history && !isIOSWebKit()) {
        history.scrollRestoration = 'manual';
      }

      this.readUrlParams();
      this.applyDefaultSort();

      this.bindScrollPersistence();

      if (window.__LS_COLLECTION_V2_BFCache) {
        this.restoredFromBFCache = true;
        this.hydrated = true;
        console.log('[CollectionV2] Restored from bfcache — skipping re-fetch');
      }

      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          this.restoredFromBFCache = true;
          this.hydrated = true;
          console.log('[CollectionV2] Restored from bfcache — skipping re-fetch');
          tryEarlyScrollRestore({ force: true });
          this.pendingScrollRestore =
            window.__LS_COLLECTION_V2_PENDING_SCROLL ?? readScrollSnapshot();
          this.restoreScrollFromSession('pageshow-bfcache');
          return;
        }

        if (shouldRestoreListingNavigation(window.location.pathname + window.location.search)) {
          this.pendingScrollRestore =
            window.__LS_COLLECTION_V2_PENDING_SCROLL ?? readScrollSnapshot();
          this.restoreScrollFromSession('pageshow');
        }
      });

      window.addEventListener('pagehide', () => {
        this.saveScrollPosition();
        this.saveSnapshot();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.saveScrollPosition();
          this.saveSnapshot();
        }
      });
      window.addEventListener('popstate', () => {
        this.readUrlParams();
        this.fetchResults();
      });

      const onProductLinkActivate = (link) => {
        setReturnIntent(
          window.location.pathname + window.location.search,
          extractProductHandleFromLink(link)
        );
        this.saveScrollPosition();
        this.saveSnapshot();
      };

      document.addEventListener('pointerdown', (e) => {
        const link = e.target.closest('#collection-product-grid a[href]');
        if (link) onProductLinkActivate(link);
      }, true);

      document.addEventListener(
        'touchstart',
        (e) => {
          const link = e.target.closest('#collection-product-grid a[href]');
          if (link) onProductLinkActivate(link);
        },
        { capture: true, passive: true }
      );

      document.addEventListener(
        'click',
        (e) => {
          const link = e.target.closest('#collection-product-grid a[href]');
          if (link) onProductLinkActivate(link);
        },
        true
      );

      if (this.restoredFromBFCache) {
        this.pendingScrollRestore =
          window.__LS_COLLECTION_V2_PENDING_SCROLL ?? readScrollSnapshot();
        if (this.pendingScrollRestore) {
          this.restoreScrollFromSession('init-bfcache');
        }
        return;
      }

      const listingUrl = window.location.pathname + window.location.search;
      const shouldRestore = shouldRestoreListingNavigation(listingUrl);

      if (!shouldRestore) {
        clearStaleListingSnapshots();
        clearReturnIntent();
      }

      this.pendingScrollRestore = shouldRestore
        ? (window.__LS_COLLECTION_V2_PENDING_SCROLL ?? readScrollSnapshot())
        : null;

      if (this.restoreSnapshot()) {
        console.log('[CollectionV2] Restored from sessionStorage snapshot');
        return;
      }

      const startTime = performance.now();
      await this.fetchResults();

      if (shouldRestore) {
        this.restoreScrollFromSession('init-fetch');
      }
      console.log(`[CollectionV2] Ready in ${(performance.now() - startTime).toFixed(0)}ms`, {
        products: this.products.length,
        total: this.totalProducts,
        facets: this.facets.length,
        page: this.page,
        totalPages: this.totalPages
      });
      console.log('=== COLLECTION V2 READY ===');
    },

    selectionMatches(a, b) {
      const af = a.filters || {};
      const bf = b.filters || {};
      const aKeys = Object.keys(af).sort();
      const bKeys = Object.keys(bf).sort();
      if (aKeys.length !== bKeys.length) return false;

      for (const key of aKeys) {
        if (!bf[key]) return false;
        const av = [...(af[key] || [])].sort();
        const bv = [...(bf[key] || [])].sort();
        if (av.length !== bv.length || av.some((v, i) => v !== bv[i])) return false;
      }

      const ap = a.price;
      const bp = b.price;
      if (!ap && !bp) return true;
      if (!ap || !bp) return false;
      return ap.min === bp.min && ap.max === bp.max;
    },

    hasPendingChanges() {
      return !this.selectionMatches(this.pending, this.applied);
    },

    revertPendingFromApplied() {
      this.pending = {
        filters: JSON.parse(JSON.stringify(this.applied.filters)),
        price: this.applied.price ? { ...this.applied.price } : null
      };
      this.filterApplicationOrder = this.buildFilterApplicationOrder(this.applied);
      this.previewCount = this.totalProducts;
    },

    clearDrawerPending() {
      this.pending = { filters: {}, price: null };
      this.filterApplicationOrder = [];
      this.lastApplied = null;
      this.isInitialLoad = false;

      this.sort = null;
      this.applyDefaultSort();
      this.refreshSortGroups();

      this.previewFilters();
    },

    closeFiltersMenuSheetApply() {
      if (this.hasPendingChanges()) {
        this.applyFilters();
      } else {
        this.cancelFiltersMenuSheet();
      }
    },

    cancelFiltersMenuSheet() {
      this.revertPendingFromApplied();
      this.closeFilterSheetShell({ intent: 'cancel' });
    },

    openFilterSheetShell(view) {
      this.sheetView = view;
      const dialog = this.$refs.filterSheetShell;
      if (!dialog?.open) {
        dialog?.showModal?.();
      }
      if (view === 'menu') {
        this.resetFiltersMenuScroll();
      }
    },

    resetFiltersMenuScroll() {
      this.$nextTick(() => {
        const el = this.$refs.filtersMenuOptionsScroll;
        if (el) el.scrollTop = 0;
      });
    },

    closeFilterSheetShell({ intent = 'cancel' } = {}) {
      this.sheetShellCloseIntent = intent;
      this.$refs.filterSheetShell?.close?.();
    },

    onFilterSheetShellCancel(event) {
      event.preventDefault();
      this.onFilterSheetShellBackdrop();
    },

    onFilterSheetShellBackdrop() {
      if (this.sheetView === 'menu') {
        this.closeFiltersMenuSheetApply();
        return;
      }

      if (this.sheetView === 'facet') {
        this.applyFilterSheet();
        return;
      }

      if (this.sheetView === 'price') {
        this.applyPriceFilterSheet();
        return;
      }

      if (this.sheetView === 'sort') {
        if (this.filterSheetFromMenu) {
          this.backToFiltersMenuFromSort();
        } else {
          this.cancelSortSheet();
        }
      }
    },

    onFilterSheetShellClose() {
      if (this.sheetShellCloseIntent === 'applied') {
        this.sheetShellCloseIntent = null;
        this.resetSheetShellState();
        return;
      }

      if (this.sheetShellCloseIntent === 'cancel') {
        if (this.sheetView !== 'menu') {
          this.revertPendingFromApplied();
        }
      } else {
        this.revertPendingFromApplied();
      }

      this.sheetShellCloseIntent = null;
      this.resetSheetShellState();
    },

    resetSheetShellState() {
      this.sheetView = null;
      this.activeFilterSheet = null;
      this.filterSheetSearchQuery = '';
      this.filterSheetFromMenu = false;
    },

    syncPendingFromApplied() {
      this.pending = {
        filters: JSON.parse(JSON.stringify(this.applied.filters)),
        price: this.applied.price ? { ...this.applied.price } : null
      };
      this.filterApplicationOrder = this.buildFilterApplicationOrder(this.applied);
      if (this.isInitialLoad) {
        this.isInitialLoad = false;
      }
      this.previewCount = this.totalProducts;
      this.previewFilters();
    },

    openFiltersMenuSheet() {
      this.filterSheetFromMenu = false;
      this.syncPendingFromApplied();
      this.openFilterSheetShell('menu');
    },

    afterChildSheetBackToMenu() {
      this.activeFilterSheet = null;
      this.filterSheetSearchQuery = '';
      this.filterSheetFromMenu = false;
    },

    getSortContext() {
      return this.isSearchPage ? 'search' : 'collection';
    },

    applyDefaultSort() {
      if (this.sort) {
        if (!this.adapter.isSortSupported(this.sort, this.getSortContext())) {
          this.sort = null;
        }
      }
      if (!this.sort && !this.isSearchPage) {
        const defaultSort = window.COLLECTION_V2_CONFIG?.defaultSort;
        if (defaultSort) {
          const canonical = this.adapter.toCanonicalSortId(defaultSort, this.getSortContext());
          if (canonical && this.adapter.isSortSupported(canonical, this.getSortContext())) {
            this.sort = canonical;
          }
        }
      }
      this.refreshSortGroups();
    },

    refreshSortGroups() {
      this.sortGroups = this.adapter.buildSortGroups(this.sort, this.getSortContext());
    },

    readUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const filters = {};
      let price = null;
      let sort = null;
      let page = 1;

      params.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          const section = normalizeFilterIdAlias(key.replace('filter.', ''));
          filters[section] = value.includes(',') ? value.split(',').map(v => v.trim()) : [value];
        } else if (key === 'price_min' || key === 'price_max') {
          if (!price) price = {};
          price[key === 'price_min' ? 'min' : 'max'] = parseFloat(value);
        } else if (key === 'sort_by') {
          sort = value;
        } else if (key === 'page') {
          page = Math.max(1, parseInt(value, 10) || 1);
        }
      });

      const hasUrlFilters = Object.keys(filters).length > 0 || price || sort || page > 1;
      this.applied = { filters, price };
      this.pending = {
        filters: JSON.parse(JSON.stringify(filters)),
        price: price ? { ...price } : null
      };
      this.sort = sort;
      this.page = page;
      this.filterApplicationOrder = this.buildFilterApplicationOrder(this.applied);
      this.isInitialLoad = !hasUrlFilters;
    },

    syncUrlParams() {
      const params = new URLSearchParams();
      const currentParams = new URLSearchParams(window.location.search);
      const searchQuery = currentParams.get('q');
      if (searchQuery) params.set('q', searchQuery);

      Object.keys(this.applied.filters).forEach(section => {
        params.set(`filter.${section}`, this.applied.filters[section].join(','));
      });

      if (this.applied.price) {
        if (this.applied.price.min != null) params.set('price_min', this.applied.price.min);
        if (this.applied.price.max != null) params.set('price_max', this.applied.price.max);
      }

      if (this.sort) params.set('sort_by', this.sort);
      if (this.page > 1) params.set('page', this.page);

      const baseUrl = window.location.pathname;
      const queryString = params.toString();
      const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      window.history.pushState({ v2: true }, '', newUrl);
      window.LSNavigationMemory?.syncCurrentIfTrackable?.();
    },

    buildFilterApplicationOrder(selection) {
      const order = [];
      Object.keys(selection.filters || {}).forEach(section => {
        if ((selection.filters[section] || []).length > 0) order.push(section);
      });
      if (selection.price && (selection.price.min != null || selection.price.max != null)) {
        order.push('price');
      }
      return order;
    },

    buildFilterParams(source = 'applied', { preview = false } = {}) {
      const selection = source === 'pending' ? this.pending : this.applied;
      const params = {};

      Object.keys(selection.filters || {}).forEach(key => {
        params[key] = selection.filters[key];
      });

      if (selection.price) {
        params.price = {};
        if (selection.price.min != null) params.price.min = selection.price.min;
        if (selection.price.max != null) params.price.max = selection.price.max;
      }

      if (this.sort) params.sort_by = this.sort;
      if (preview) params.__preview = true;
      if (this.baselineFilters?.length) params.__baselineFilters = this.baselineFilters;

      return params;
    },

    async fetchCollectionData(filterParams, page) {
      if (this.isSearchPage) {
        return this.adapter.getCollectionData(null, filterParams, page, this.listingQuery);
      }
      return this.adapter.getCollectionData(this.listingQuery, filterParams, page);
    },

    async fetchResults({ silent = false } = {}) {
      this.restoredFromBFCache = false;

      const requestId = ++this.fetchRequestId;
      const requestedPage = this.page;

      if (!silent) {
        this.loading = true;
        this.renderer.setLoadingState(true);
      }

      try {
        const filterParams = this.buildFilterParams('applied');
        const data = await this.fetchCollectionData(filterParams, requestedPage);

        if (requestId !== this.fetchRequestId) return;
        if (requestedPage !== this.page) return;

        this.products = data.products || [];
        this.facets = data.filters || [];
        this.totalProducts = data.products_count_total ?? this.products.length;
        this.previewCount = this.totalProducts;
        this.totalPages = data.pagination?.total_pages || 0;

        if (!this.baselineFilters.length && this.facets.length) {
          this.baselineFilters = JSON.parse(JSON.stringify(this.facets));
        }

        if (!silent) {
          this.renderer.render(this.products);
          this.hydrated = true;
          this.lastRenderedPage = requestedPage;
        }

        this.refreshSortGroups();
        console.log('[CollectionV2] Fetch complete', {
          products: this.products.length,
          total: this.totalProducts,
          page: requestedPage,
          totalPages: this.totalPages,
          silent
        });
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        console.error('[CollectionV2] fetch error:', error);
        if (!silent) this.renderer.renderEmpty('Error loading products. Please try again.');
      } finally {
        if (!silent && requestId === this.fetchRequestId) {
          this.loading = false;
          this.renderer.setLoadingState(false);
          if (this.pendingScrollRestore) {
            this.restoreScrollFromSession('fetch-rendered');
          }
        }
      }
    },

    async previewFilters() {
      const requestId = ++this.previewRequestId;
      try {
        const data = await this.fetchCollectionData(this.buildFilterParams('pending', { preview: true }), 1);
        if (requestId !== this.previewRequestId) return;
        this.previewCount = data.products_count_total ?? 0;
        if (data.filters) this.facets = data.filters;
      } catch (error) {
        console.error('[CollectionFilterV2] preview error:', error);
      }
    },

    toggleFacet(facetId, value) {
      if (this.isPending(facetId, value)) {
        this.pending.filters[facetId] = (this.pending.filters[facetId] || []).filter(v => v !== value);
        if (!this.pending.filters[facetId].length) {
          delete this.pending.filters[facetId];
          this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== facetId);
        }
      } else {
        const values = (this.pending.filters[facetId] || []).filter(v => v !== value);
        values.unshift(value);
        this.pending.filters = { ...this.pending.filters, [facetId]: values };
        this.lastApplied = { section: facetId, value };
        this.touchBarItemOrder(facetId);
      }

      this.isInitialLoad = false;
      this.previewFilters();
    },

    updatePendingPrice(field, rawValue) {
      const hadPrice = Boolean(this.pending.price);
      if (!this.pending.price) this.pending.price = {};
      this.pending.price[field] = rawValue ? parseFloat(rawValue) : null;

      if (!this.pending.price.min && !this.pending.price.max) {
        this.pending.price = null;
        this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== 'price');
      } else {
        this.lastApplied = { section: 'price', value: 'range' };
        if (!hadPrice) this.filterApplicationOrder.push('price');
      }

      clearTimeout(this.priceDebounce);
      this.priceDebounce = setTimeout(() => this.previewFilters(), 500);
    },

    isPending(facetId, value) {
      return (this.pending.filters[facetId] || []).includes(value);
    },

    isApplied(facetId, value) {
      return (this.applied.filters[facetId] || []).includes(value);
    },

    appliedCount(facetId) {
      if (facetId === 'price' && this.applied.price) return 1;
      return (this.applied.filters[facetId] || []).length;
    },

    pendingCount(facetId) {
      if (facetId === 'price' && this.pending.price) return 1;
      return (this.pending.filters[facetId] || []).length;
    },

    removePendingFilter(section, value) {
      if (section === 'price') {
        this.pending.price = null;
        this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== 'price');
      } else {
        this.pending.filters[section] = (this.pending.filters[section] || []).filter(v => v !== value);
        if (!this.pending.filters[section]?.length) {
          delete this.pending.filters[section];
          this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== section);
        }
      }
      this.previewFilters();
    },

    async applyFilters() {
      if (this.hasPendingChanges()) {
        const section = this.openAccordion || this.lastApplied?.section;
        if (section) this.touchBarItemOrder(section);
      }

      this.applied = {
        filters: JSON.parse(JSON.stringify(this.pending.filters)),
        price: this.pending.price ? { ...this.pending.price } : null
      };
      this.page = 1;
      this.isInitialLoad = false;
      this.syncUrlParams();
      this.scrollToTop();
      this.scrollFilterBarToStart();

      this.closeFilterSheetShell({ intent: 'applied' });

      await this.fetchResults();
      this.settleScrollToTop();
    },

    async clearAllFilters() {
      this.pending = { filters: {}, price: null };
      this.applied = { filters: {}, price: null };
      this.filterApplicationOrder = [];
      this.lastApplied = null;
      this.page = 1;
      this.sort = null;
      this.applyDefaultSort();
      this.refreshSortGroups();
      this.syncUrlParams();
      this.scrollToTop();

      this.closeFilterSheetShell({ intent: 'applied' });

      await this.fetchResults();
      this.settleScrollToTop();
    },

    async setSort(sortId) {
      this.sort = sortId;
      this.page = 1;
      this.refreshSortGroups();
      this.syncUrlParams();
      this.scrollToTop();
      this.scrollFilterBarToStart();

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'collection_sort', sort_by: sortId });

      await this.fetchResults();
      this.settleScrollToTop();
    },

    async goToPage(n) {
      if (this.loading) return;

      const target = Math.max(1, Math.min(n, this.totalPages || 1));
      if (target === this.page && this.lastRenderedPage === target) return;

      this.page = target;
      this.syncUrlParams();
      this.scrollToTop();
      await this.fetchResults();
      this.settleScrollToTop();
    },

    beginProgrammaticScroll(scrollY = 0) {
      this.programmaticScrollUntil = Date.now() + PROGRAMMATIC_SCROLL_GUARD_MS;
      this.pendingScrollRestore = null;
      try {
        const url = canonicalizeListingUrl(window.location.pathname + window.location.search);
        sessionStorage.setItem(
          SCROLL_SNAPSHOT_KEY,
          JSON.stringify({ url, scrollY, savedAt: Date.now() })
        );
      } catch (e) {}
    },

    scrollToTop() {
      const y = getListingChromeScrollY();
      this.beginProgrammaticScroll(y);
      if (isSafariEngine()) {
        applyScrollPosition(y);
        requestAnimationFrame(() => applyScrollPosition(y));
        return;
      }
      window.scrollTo({ top: y, behavior: 'smooth' });
    },

    settleScrollToTop() {
      const y = getListingChromeScrollY();
      const durationMs = isSafariEngine() ? 1800 : 1200;
      this.beginProgrammaticScroll(y);
      this.programmaticScrollUntil = Date.now() + durationMs;
      if (y <= 0) {
        applyScrollPosition(0);
        return;
      }
      restoreScrollPosition(y, { durationMs, onlyCorrectUpwardDrift: true });
    },

    scrollFilterBarToStart() {
      this.$nextTick(() => {
        const scrollEl = document.querySelector('[data-filter-bar-scroll]');
        if (scrollEl) scrollEl.scrollLeft = 0;
      });
    },

    touchBarItemOrder(section) {
      if (!section) return;
      this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== section);
      this.filterApplicationOrder.push(section);
    },

    openFilterSheetFromMenu(facetId) {
      this.filterSheetFromMenu = true;
      this.openFilterSheet(facetId, { fromMenu: true });
    },

    openPriceFilterSheetFromMenu() {
      this.filterSheetFromMenu = true;
      this.openPriceFilterSheet({ fromMenu: true });
    },

    openSortSheetFromMenu() {
      this.filterSheetFromMenu = true;
      this.openSortSheet({ fromMenu: true });
    },

    backToFiltersMenu() {
      this.afterChildSheetBackToMenu();
      this.openFilterSheetShell('menu');
    },

    backToFiltersMenuFromPrice() {
      this.backToFiltersMenu();
    },

    backToFiltersMenuFromSort() {
      this.backToFiltersMenu();
    },

    shouldShowFilter(filter) {
      const id = (filter.id || '').toLowerCase();
      const label = (filter.label || '').toLowerCase();
      if (id === 'availability' || label.includes('availability')) return false;
      if (filter.type === 'price_range') return true;
      return this.visibleValues(filter).length > 1;
    },

    shouldShowQuickFilter(facet) {
      if (!facet) return false;
      if (this.appliedCount(facet.id) > 0) return true;
      return this.visibleValues(facet).length > 1;
    },

    filterSheetValueMatchesQuery(value, query) {
      const label = (value.label || value.value || '').toLowerCase();
      const rawValue = (value.value || '').toLowerCase();
      return label.includes(query) || rawValue.includes(query);
    },

    filterSelectableValues(facetId, values) {
      return (values || []).filter(value => {
        if ((value.count || 0) > 0) return true;
        return this.isPending(facetId, value.value) || this.isApplied(facetId, value.value);
      });
    },

    visibleGroups(facet) {
      if (facet.type !== 'grouped_option' || !facet.groups?.length) return [];
      return facet.groups
        .map(group => ({
          ...group,
          values: this.filterSelectableValues(facet.id, group.values)
        }))
        .filter(group => group.values.length > 0);
    },

    visibleValues(facet) {
      if (facet.type === 'price_range') return [];
      if (facet.type === 'grouped_option' && facet.groups?.length) {
        return this.visibleGroups(facet).flatMap(group => group.values);
      }
      return this.filterSelectableValues(facet.id, facet.values);
    },

    isLongList(facet) {
      if (facet.type === 'grouped_option') {
        return this.visibleGroups(facet).reduce((sum, group) => sum + group.values.length, 0) > LONG_LIST_THRESHOLD;
      }
      return this.visibleValues(facet).length > LONG_LIST_THRESHOLD;
    },

    isSizeFacet(facet) {
      return facet?.id === 'size';
    },

    isColourFacet(facet) {
      return facet?.id === 'colour';
    },

    colourSwatch(name) {
      return resolveColourSwatch(name);
    },

    colourSwatchClass(name) {
      const swatch = resolveColourSwatch(name);
      if (swatch.multi) return 'colour-filter__swatch--multi';
      return swatch.bordered ? 'colour-filter__swatch--border' : '';
    },

    colourSwatchStyle(name) {
      const swatch = resolveColourSwatch(name);
      if (swatch.multi) return {};
      return { background: swatch.background };
    },

    getPriceFacet() {
      return this.facets.find(f => f.id === 'price' && f.type === 'price_range');
    },

    bindScrollPersistence() {
      window.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.scrollSaveTimer);
          this.scrollSaveTimer = setTimeout(() => this.saveScrollPosition(), 150);
        },
        { passive: true }
      );
    },

    saveScrollPosition() {
      if (Date.now() < this.programmaticScrollUntil || isProgrammaticScroll()) return;

      try {
        const url = canonicalizeListingUrl(window.location.pathname + window.location.search);
        sessionStorage.setItem(
          SCROLL_SNAPSHOT_KEY,
          JSON.stringify({ url, scrollY: currentScrollY(), savedAt: Date.now() })
        );
      } catch (e) {}
    },

    markScrollRestoreComplete() {
      this.pendingScrollRestore = null;
      this.scrollRestoreFinalizeTimer = null;
      clearReturnIntent();
      cancelActiveScrollRestore();
    },

    restoreScrollFromSession(reason) {
      const scrollY = this.pendingScrollRestore ?? readScrollSnapshot();
      if (scrollY == null || scrollY <= 0) return;

      if (Math.abs(currentScrollY() - scrollY) <= 8) {
        this.markScrollRestoreComplete();
        return;
      }

      const isLateCorrection = reason === 'fetch-rendered' || reason === 'snapshot-silent-fetch';
      if (isLateCorrection) {
        if (activeScrollRestore) return;
        restoreScrollPosition(scrollY, {
          durationMs: 400,
          onlyCorrectUpwardDrift: true,
          onUserTakeover: () => this.markScrollRestoreComplete()
        });
        return;
      }

      if (this.scrollRestoreFinalizeTimer) {
        clearTimeout(this.scrollRestoreFinalizeTimer);
      }

      const durationMs = isIOSWebKit() ? 1800 : 1200;
      console.log('[CollectionV2] Restoring scroll', { reason, scrollY, durationMs });
      restoreScrollPosition(scrollY, {
        durationMs,
        onUserTakeover: () => this.markScrollRestoreComplete()
      });

      this.scrollRestoreFinalizeTimer = setTimeout(() => {
        if (activeScrollRestore) return;
        if (Math.abs(currentScrollY() - scrollY) > 8) {
          tryScrollToLastProductCard(scrollY);
        }
        if (Math.abs(currentScrollY() - scrollY) <= 8) {
          this.markScrollRestoreComplete();
        }
      }, durationMs + 50);
    },

    saveSnapshot() {
      this.saveScrollPosition();

      const grid = document.getElementById('collection-product-grid');
      if (!grid || !this.hydrated) return;

      try {
        const url = canonicalizeListingUrl(window.location.pathname + window.location.search);
        const scrollY = currentScrollY();
        const savedAt = Date.now();

        const snapshot = {
          url,
          scrollY,
          savedAt,
          state: {
            pending: this.pending,
            applied: this.applied,
            sort: this.sort,
            page: this.page,
            totalPages: this.totalPages,
            totalProducts: this.totalProducts,
            facets: this.facets,
            sortGroups: this.sortGroups,
            filterApplicationOrder: this.filterApplicationOrder
          },
          gridHtml: grid.innerHTML
        };
        sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      } catch (e) {}
    },

    restoreSnapshot() {
      try {
        const raw = sessionStorage.getItem(SNAPSHOT_KEY);
        if (!raw) return false;

        const snapshot = JSON.parse(raw);
        const currentUrl = window.location.pathname + window.location.search;

        if (!urlsMatchExactly(snapshot.url, currentUrl)) return false;
        if (Date.now() - snapshot.savedAt > SNAPSHOT_MAX_AGE_MS) return false;

        const state = snapshot.state || {};
        this.pending = state.pending || { filters: {}, price: null };
        this.applied = state.applied || { filters: {}, price: null };
        this.sort = state.sort || null;
        this.page = state.page || 1;
        this.totalPages = state.totalPages || 0;
        this.totalProducts = state.totalProducts || 0;
        this.previewCount = this.totalProducts;
        this.facets = state.facets || [];
        this.sortGroups = state.sortGroups || [];
        this.filterApplicationOrder = state.filterApplicationOrder || [];
        this.isInitialLoad = false;

        this.renderer.restoreHtml(snapshot.gridHtml);
        this.hydrated = true;
        this.lastRenderedPage = this.page;
        this.refreshSortGroups();

        const savedScrollY = snapshot.scrollY || 0;
        this.pendingScrollRestore = savedScrollY;
        this.restoreScrollFromSession('snapshot-grid');

        this.fetchResults({ silent: true }).then(() => {
          const gridHasProducts = Boolean(
            this.renderer.container?.querySelector('[data-product-id]')
          );
          if (this.products.length && !gridHasProducts) {
            this.renderer.render(this.products);
            this.lastRenderedPage = this.page;
          }
          this.restoreScrollFromSession('snapshot-silent-fetch');
        });

        return true;
      } catch (e) {
        return false;
      }
    },

    restoreScrollOnly() {
      this.restoreScrollFromSession('scroll-only');
    },

    openFilterSheet(facetId, { fromMenu = false } = {}) {
      if (!fromMenu) {
        this.filterSheetFromMenu = false;
        this.syncPendingFromApplied();
      }

      this.filterSheetSearchQuery = '';
      this.activeFilterSheet = facetId;
      this.openFilterSheetShell('facet');
    },

    cancelFilterSheet() {
      this.closeFilterSheetShell({ intent: 'cancel' });
    },

    async applyFilterSheet() {
      if (!this.hasPendingChanges()) {
        this.cancelFilterSheet();
        return;
      }

      if (this.activeFilterSheet) {
        this.touchBarItemOrder(this.activeFilterSheet);
      }

      this.applied = {
        filters: JSON.parse(JSON.stringify(this.pending.filters)),
        price: this.pending.price ? { ...this.pending.price } : null
      };
      this.page = 1;
      this.isInitialLoad = false;
      this.syncUrlParams();
      this.scrollToTop();
      this.scrollFilterBarToStart();

      this.closeFilterSheetShell({ intent: 'applied' });

      await this.fetchResults();
      this.settleScrollToTop();
    },

    clearFilterSheetFacet() {
      const facetId = this.activeFilterSheet;
      if (!facetId) return;

      delete this.pending.filters[facetId];
      this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== facetId);
      this.isInitialLoad = false;
      this.previewFilters();
    },

    openSortSheet({ fromMenu = false } = {}) {
      if (!fromMenu) {
        this.filterSheetFromMenu = false;
        this.syncPendingFromApplied();
      }

      this.openFilterSheetShell('sort');
    },

    cancelSortSheet() {
      this.closeFilterSheetShell({ intent: 'cancel' });
    },

    closeSortSheetAfterSelect() {
      if (this.filterSheetFromMenu) {
        this.backToFiltersMenu();
        return;
      }

      this.closeFilterSheetShell({ intent: 'cancel' });
    },

    openPriceFilterSheet({ fromMenu = false } = {}) {
      if (!fromMenu) {
        this.filterSheetFromMenu = false;
        this.syncPendingFromApplied();
      }

      this.openFilterSheetShell('price');
    },

    cancelPriceFilterSheet() {
      this.closeFilterSheetShell({ intent: 'cancel' });
    },

    async applyPriceFilterSheet() {
      if (!this.hasPendingChanges()) {
        this.cancelPriceFilterSheet();
        return;
      }

      if (this.pending.price) {
        this.touchBarItemOrder('price');
      }

      this.applied = {
        filters: JSON.parse(JSON.stringify(this.pending.filters)),
        price: this.pending.price ? { ...this.pending.price } : null
      };
      this.page = 1;
      this.isInitialLoad = false;
      this.syncUrlParams();
      this.scrollToTop();
      this.scrollFilterBarToStart();

      this.closeFilterSheetShell({ intent: 'applied' });

      await this.fetchResults();
      this.settleScrollToTop();
    },

    clearPriceFilterSheet() {
      this.pending.price = null;
      this.filterApplicationOrder = this.filterApplicationOrder.filter(s => s !== 'price');
      this.isInitialLoad = false;
      this.previewFilters();
    },

    getSaveableUrl() {
      const params = new URLSearchParams();
      const currentParams = new URLSearchParams(window.location.search);
      const searchQuery = currentParams.get('q');
      if (searchQuery) params.set('q', searchQuery);

      Object.keys(this.applied.filters).forEach(section => {
        params.set(`filter.${section}`, this.applied.filters[section].join(','));
      });

      if (this.applied.price) {
        if (this.applied.price.min != null) params.set('price_min', this.applied.price.min);
        if (this.applied.price.max != null) params.set('price_max', this.applied.price.max);
      }

      if (this.sort) params.set('sort_by', this.sort);

      const baseUrl = window.location.pathname;
      const queryString = params.toString();
      const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
      return canonicalizeListingUrl(newUrl);
    },

    resetSaveSearchModal() {
      if (this.saveSearchCloseTimer) {
        clearTimeout(this.saveSearchCloseTimer);
        this.saveSearchCloseTimer = null;
      }
      this.saveSearchSubmitting = false;
      this.saveSearchError = '';
      this.saveSearchNameLength = 0;
      this.saveSearchSuccess = false;
      this.saveSearchSuccessName = '';
      this.saveSearchDissolving = false;
      const input = document.getElementById('saved-search-name-input');
      if (input) input.value = '';
    },

    openSaveSearchModal() {
      this.resetSaveSearchModal();
      const modal = document.getElementById('saved-search-save-modal');
      const input = document.getElementById('saved-search-name-input');
      if (modal && typeof modal.showModal === 'function') {
        modal.showModal();
        requestAnimationFrame(() => input?.focus());
      }
    },

    closeSaveSearchModalAfterSuccess() {
      const modal = document.getElementById('saved-search-save-modal');
      this.saveSearchDissolving = true;
      this.saveSearchCloseTimer = setTimeout(() => {
        modal?.close();
        this.resetSaveSearchModal();
      }, 300);
    },

    async submitSaveSearch() {
      const input = document.getElementById('saved-search-name-input');
      const name = String(input?.value || '').trim();
      if (!name) {
        this.saveSearchError = 'Name is required.';
        return;
      }

      if (!window.SavedSearchesLS) {
        this.saveSearchError = 'Saved searches are unavailable.';
        return;
      }

      this.saveSearchSubmitting = true;
      this.saveSearchError = '';

      try {
        await window.SavedSearchesLS.ready;
        await window.SavedSearchesLS.create(name, this.getSaveableUrl());
        this.saveSearchSuccessName = name;
        this.saveSearchSuccess = true;
        this.saveSearchSubmitting = false;
        if (this.saveSearchCloseTimer) clearTimeout(this.saveSearchCloseTimer);
        this.saveSearchCloseTimer = setTimeout(() => {
          this.closeSaveSearchModalAfterSuccess();
        }, 2500);
      } catch (error) {
        this.saveSearchError = error?.message || 'Could not save search.';
        this.saveSearchSubmitting = false;
      }
    }
  }));
});
